/**
 * Darstellung einer Ticket-Beschreibung im ITIL-Format.
 *
 * KIAN (Aufnahme, „KIAN: ITIL-Format", Import) schreibt Beschreibungen als Folge von
 * `<p><strong>Label:</strong> Inhalt</p>` — in weclapp und im Leistungsnachweis muss das
 * so bleiben. Im ServiceHub las sich das als Fließtext mit fetten Wörtern: Die Labels
 * gingen im Text unter, und die Hinweise unten („✏️ … bearbeitet", „📥 Übernommen aus")
 * sahen aus wie Teil des Anliegens. Deshalb baut die Anzeige daraus Zeilen mit
 * Label-Spalte und setzt die Hinweise als Fußzeile ab — nur in der Anzeige, der
 * gespeicherte Text bleibt unverändert.
 *
 * Die Labels stehen auch in `backend/src/services/kianTicketIntakeService.ts`
 * (`TYPE_TEMPLATES`) — kommt dort eines dazu, hier ergänzen, sonst bleibt es Fließtext.
 */

/** Labels der vier ITIL-Schablonen plus „Anlagen" (Beschreibungs-Optimierung). */
export const ITIL_LABELS: readonly string[] = [
  // Störung
  'Symptom',
  'Betroffen',
  'Auswirkung',
  'Bisherige Maßnahmen',
  'Ziel',
  // Auftrag
  'Anliegen',
  'Umfang',
  'Voraussetzungen',
  'Abnahmekriterien',
  'Termine',
  // Wartung
  'Anlass',
  'Geplante Arbeiten',
  'Befund',
  'Empfehlungen',
  // Beratung
  'Anlass & Teilnehmer',
  'Besprochene Themen',
  'Ergebnisse & Empfehlungen',
  'Vereinbarte nächste Schritte',
  // Optimierung
  'Anlagen',
];

const LABEL_SET = new Set(ITIL_LABELS.map((l) => l.toLowerCase()));

/** Ab so vielen erkannten Labels gilt eine Beschreibung als ITIL-gegliedert. */
const MIN_LABELS = 2;

/**
 * Zerlegt „Label: Inhalt" — nur für bekannte ITIL-Labels, sonst null. Ein beliebiges
 * „Wort:" am Absatzanfang (Mail: „Hallo zusammen:", „Hinweis:") bleibt Fließtext.
 */
export function splitItilLabel(text: string): { label: string; rest: string } | null {
  const m = text.trimStart().match(/^([^:\n]{2,40}):\s*/);
  if (!m) return null;
  const label = m[1].trim();
  if (!LABEL_SET.has(label.toLowerCase())) return null;
  return { label, rest: text.trimStart().slice(m[0].length) };
}

/** Hinweis-Zeilen, die die Portal-Automatik an die Beschreibung hängt. */
const NOTE_MARKERS = ['✏️', '✏', '📥', '🤖'];

export function isDescriptionNote(text: string): boolean {
  const t = text.trimStart();
  return NOTE_MARKERS.some((m) => t.startsWith(m));
}

/**
 * Ein Hinweis kann mehrere Sätze mit eigenem Symbol tragen („✏️ … gesichert. 🤖 Die
 * Beschreibung wurde von KIAN …") — je Symbol eine Zeile.
 */
export function splitDescriptionNote(text: string): string[] {
  return text
    .split(/\s*(?=🤖|📥)/u)
    .map((s) => s.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

export interface DescriptionLayoutClasses {
  itil: string;
  row: string;
  label: string;
  body: string;
  notes: string;
  note: string;
}

/**
 * Baut aus bereits bereinigtem HTML (DOMPurify) die gegliederte Fassung.
 * Liefert null, wenn nichts zu gliedern ist — dann bleibt die Anzeige wie bisher.
 */
export function layoutTicketDescription(
  cleanHtml: string,
  classes: DescriptionLayoutClasses
): string | null {
  if (typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(`<div>${cleanHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return null;

  const nodes = Array.from(root.childNodes).filter(
    (n) => !(n.nodeType === Node.TEXT_NODE && !(n.textContent ?? '').trim())
  );
  const text = (n: Node) => n.textContent ?? '';
  const isPara = (n: Node): n is HTMLElement =>
    n.nodeType === Node.ELEMENT_NODE && ['P', 'DIV'].includes((n as HTMLElement).tagName);

  const labelCount = nodes.filter((n) => isPara(n) && splitItilLabel(text(n))).length;
  const noteNodes = nodes.filter((n) => isPara(n) && isDescriptionNote(text(n)));
  if (labelCount < MIN_LABELS && noteNodes.length === 0) return null;

  const out = doc.createElement('div');
  const structured = labelCount >= MIN_LABELS;
  if (structured) out.className = classes.itil;
  let currentBody: HTMLElement | null = null;

  for (const node of nodes) {
    if (isPara(node) && isDescriptionNote(text(node))) {
      currentBody = null;
      continue; // kommt gesammelt in die Fußzeile
    }
    const split = structured && isPara(node) ? splitItilLabel(text(node)) : null;
    if (split && isPara(node)) {
      const row = doc.createElement('div');
      row.className = classes.row;
      const label = doc.createElement('div');
      label.className = classes.label;
      label.textContent = split.label;
      const body = doc.createElement('div');
      body.className = classes.body;
      stripLeadingLabel(node, split.label);
      if (text(node).trim()) body.appendChild(node);
      row.append(label, body);
      out.appendChild(row);
      currentBody = body;
      continue;
    }
    // Spiegelstriche und Folgeabsätze gehören zum Block darüber.
    if (currentBody) currentBody.appendChild(node);
    else out.appendChild(node);
  }

  if (noteNodes.length > 0) {
    const notes = doc.createElement('div');
    notes.className = classes.notes;
    for (const n of noteNodes) {
      for (const line of splitDescriptionNote(text(n))) {
        const p = doc.createElement('p');
        p.className = classes.note;
        p.textContent = line;
        notes.appendChild(p);
      }
    }
    out.appendChild(notes);
  }
  return out.innerHTML === '' ? null : out.outerHTML;
}

/**
 * Entfernt „Label:" vom Anfang des Absatzes — aus dem führenden <strong>/<b> oder, bei
 * Klartext-Absätzen der Aufnahme („Symptom: …"), aus dem ersten Textknoten.
 */
function stripLeadingLabel(p: HTMLElement, label: string): void {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`^\\s*${escaped}\\s*:?\\s*`, 'i');
  let first: ChildNode | null = p.firstChild;
  while (first && first.nodeType === Node.TEXT_NODE && !(first.textContent ?? '').trim()) {
    const next: ChildNode | null = first.nextSibling;
    first.remove();
    first = next;
  }
  if (!first) return;
  if (first.nodeType === Node.ELEMENT_NODE && ['STRONG', 'B'].includes((first as HTMLElement).tagName)) {
    const inner = (first.textContent ?? '').replace(re, '');
    if (inner.trim()) {
      first.textContent = inner;
      return;
    }
    first.remove();
    // Doppelpunkt kann auch hinter dem </strong> stehen: „<strong>Label</strong>: …"
    const after = p.firstChild;
    if (after && after.nodeType === Node.TEXT_NODE) {
      after.textContent = (after.textContent ?? '').replace(/^\s*:?\s*/, '');
    }
    return;
  }
  if (first.nodeType === Node.TEXT_NODE) {
    first.textContent = (first.textContent ?? '').replace(re, '');
  }
}
